import { DateTime } from 'luxon';
import { useState, useCallback, useMemo, useEffect } from 'react';

function getRemainingSeconds(until: DateTime) {
  const diffInSeconds = DateTime.now().diff(until, 'seconds').seconds;
  return Math.round(diffInSeconds) * -1;
}

interface Options {
  onElapsed?: () => void;
}

export function useCountdownTimer({ onElapsed }: Options = {}) {
  const [timestamp, setTimestamp] = useState<string>();
  const [secondsRemaining, setSecondsRemaining] = useState<number>(0);

  const dateTime = useMemo(() => {
    if (timestamp) {
      return DateTime.fromISO(timestamp);
    }
    return undefined;
  }, [timestamp]);

  const reset = useCallback(() => {
    setTimestamp(undefined);
  }, []);

  const set = useCallback((seconds: number) => {
    setTimestamp(DateTime.now().plus({ seconds }).toISO());
  }, []);

  useEffect(() => {
    if (dateTime) {
      setSecondsRemaining(getRemainingSeconds(dateTime));
    }

    const interval = setInterval(() => {
      if (!dateTime) {
        return;
      }

      const secondsRemaining = getRemainingSeconds(dateTime);
      if (secondsRemaining >= 1) {
        setSecondsRemaining(secondsRemaining);
        return;
      }

      setSecondsRemaining(0);
      clearInterval(interval);

      if (onElapsed) {
        onElapsed();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [dateTime, onElapsed]);

  return { set, reset, secondsRemaining };
}
